import {
  type AnyClock,
  type Clock,
  type ClockDurationType,
  type ClockTimeType,
  PerformanceClock,
} from "@repo/lib/clock";
import {
  type AnyKeyHasher,
  type KeyHasher,
  type KeyHasherKeyType,
  type KeyHasherPatternType,
  StringKeyHasher,
} from "@repo/lib/keyHasher";

type Key<H extends AnyKeyHasher> = KeyHasherKeyType<H>;
type Pattern<H extends AnyKeyHasher> = KeyHasherPatternType<H>;
type Time<C extends AnyClock> = ClockTimeType<C>;
type Duration<C extends AnyClock> = ClockDurationType<C>;

export type DeleteHandler<H extends KeyHasher<Key<H>, Pattern<H>>> = (
  /** The key of the entry that expired. */
  key: Key<H>,
  /** The hash-string of the key of the expired entry. This can be used for optimizing object
   * cache deletion if the object table uses the same key hasher. */
  hashedKey: string
) => void;

export interface ExpirationCacheOptions<
  H extends KeyHasher<Key<H>, Pattern<H>>,
  C extends Clock<Time<C>, Duration<C>>,
> {
  /** A handler to be called after an entry is expired. The key (and hashed key) is provided to
   * aid in deleting any associated record(s) in a corresponding table.
   *
   * Note: This callback is not triggered immediately on data expiration. Instead, data is
   * purged after a given delay when any method on the class is called. At that point, if the
   * data happens to be expired, the delete callback is triggered. */
  onDelete?: DeleteHandler<H>;
  /** The default time after which entries expire. Use false to indicate entries should not
   * expire by default. Default: false */
  defaultExpirationTime?: number | false;
  /** The time after which auto-purging will occur on any request. Set to null to disable
   * automatic purging (Not recommended). Defaults to 300 seconds (5 minutes). */
  purgeAfter?: number | null;
  /** A key hasher for hashing input keys. Defaults to a StringKeyHasher. */
  keyHasher?: H;
  /** A clock for handling expiration times. */
  clock?: C;
}

/** A class for storing expiration times and triggering deletion after expiration.
 *
 * This class does not contain cached data itself. Instead, it manages expiration times for data
 * by key, and offers a delete callback that can be used for deleting data from an upper-level
 * cache service after the data expires. */
export class ExpirationCache<
  H extends KeyHasher<Key<H>, Pattern<H>>,
  C extends Clock<Time<C>, Duration<C>>,
> {
  private hasher: H;
  private clock: C;
  private onDelete?: DeleteHandler<H>;
  private defaultExpirationTime: number | false;
  private purgeAfter: Duration<C> | null;
  private nextPurge: Time<C> | null;

  private expireTimes = new Map<string, Time<C> | false>();

  constructor({
    onDelete,
    defaultExpirationTime = false,
    purgeAfter = 60 * 5,
    keyHasher = new StringKeyHasher() as H,
    clock = new PerformanceClock() as C,
  }: ExpirationCacheOptions<H, C> = {}) {
    this.hasher = keyHasher;
    this.clock = clock;

    this.onDelete = onDelete;
    this.defaultExpirationTime = defaultExpirationTime;
    this.purgeAfter =
      purgeAfter == null ? null : this.clock.fromSeconds(purgeAfter);
    this.nextPurge =
      this.purgeAfter == null ? null : this.clock.addToNow(this.purgeAfter);
  }

  /** Trigger deletion of an expired key. */
  private expireKey(strKey: string, key?: Key<H>) {
    // delete
    this.expireTimes.delete(strKey);
    // trigger onDelete handler
    if (this.onDelete) {
      key ??= this.hasher.unhash(strKey);
      this.onDelete(key, strKey);
    }
  }

  /** Run a check and purge expired cache immediately. */
  purgeExpired() {
    // get now, and update next purge time
    const now = this.clock.now();
    if (this.purgeAfter == null) {
      this.nextPurge = null;
    } else {
      this.nextPurge = this.clock.add(now, this.purgeAfter);
    }

    // iterate cache and purge any expired entries
    for (const [strKey, expires] of this.expireTimes) {
      if (expires !== false && this.clock.isBefore(expires, now)) {
        this.expireKey(strKey);
      }
    }
  }

  /** Check to see if cache needs to be purged. If so, run the cache purging process. */
  tryPurgeExpired() {
    if (this.nextPurge != null && this.clock.isBeforeNow(this.nextPurge)) {
      this.purgeExpired();
    }
  }

  /** Insert an expiration entry. Provide a length of time in seconds for the entry to persist.
   * If false is provided, the entry will not expire, and will require manual deletion. */
  set(key: Key<H>, expiresAfter?: number | false) {
    this.tryPurgeExpired();

    expiresAfter ??= this.defaultExpirationTime;

    const strKey = this.hasher.hash(key);
    const expiresAt =
      expiresAfter === false
        ? false
        : this.clock.addToNow(this.clock.fromSeconds(expiresAfter));
    this.expireTimes.set(strKey, expiresAt);
  }

  /** Insert an expiration entry. Provide a scheduled date for the cache to expire once it is
   * reached. If false is provided, the entry will not expire, and will require manual
   * deletion. */
  schedule(key: Key<H>, expirationTime: Date | false) {
    this.tryPurgeExpired();

    const strKey = this.hasher.hash(key);
    const expiresAt =
      expirationTime === false ? false : this.clock.fromDate(expirationTime);
    this.expireTimes.set(strKey, expiresAt);
  }

  /** Get the expiration time for the key as a date. Returns false if the entry does not expire.
   * Returns undefined if the entry does not exist **or if it already expired**.
   *
   * **Notice:** Expiration times in the past will never be returned, because attempting to get
   * an expiration time that is expired will trigger expiration purge. */
  get(key: Key<H>): Date | false | undefined {
    this.tryPurgeExpired();

    const strKey = this.hasher.hash(key);
    const expires = this.expireTimes.get(strKey);
    if (expires === undefined || expires === false) {
      // typescript thinks Time<C> could be undefined since it's unknown: force-cast
      return expires as undefined | false;
    }

    // if this entry expired, trigger deletion and return undefined
    if (this.clock.isBeforeNow(expires)) {
      this.expireKey(strKey, key);
      return undefined;
    }

    return this.clock.toDate(expires);
  }

  /** Check if an entry is expired. */
  isExpired(key: Key<H>) {
    this.tryPurgeExpired();

    const strKey = this.hasher.hash(key);
    const expires = this.expireTimes.get(strKey);

    if (expires === undefined) return true; // not set
    if (expires === false) return false; // never expires

    // if an existing entry expired, delete and return true
    if (this.clock.isBeforeNow(expires)) {
      this.expireKey(strKey, key);
      return true;
    }
    // exists, but didn't expire
    return false;
  }

  /** Delete an entry from the expiration cache. Returns true if deletion was successful.
   * Returns false if the entry did not exist. Note, it will also return true for deleted
   * entries even if they already expired but remained in the cache.
   *
   * Pass true for `triggerDelete` to trigger the `onDelete` handler if the cached entry existed
   * during deletion. `triggerDelete` defaults to false. */
  delete(key: Key<H>, triggerDelete: boolean = false) {
    try {
      const strKey = this.hasher.hash(key);
      const expires = this.expireTimes.get(strKey);
      if (expires === undefined) return false;

      this.expireTimes.delete(strKey);
      if (triggerDelete && this.onDelete) {
        this.onDelete(key, strKey);
      }

      return true;
    } finally {
      // try purging expired cache after:
      // otherwise, `onDelete` might trigger for this entry even when `triggerDelete` is false
      this.tryPurgeExpired();
    }
  }

  /** Clears all the cached timeouts. If 'triggerDeletions' is true, the onDelete handler will
   * be called for all entries in the cache. */
  clear(triggerDeletions: boolean = true) {
    if (triggerDeletions && this.onDelete) {
      const keys = [...this.expireTimes.keys()];
      this.expireTimes.clear();

      for (const strKey of keys) {
        this.expireKey(strKey);
      }
    } else {
      this.expireTimes.clear();
    }
  }

  /** Get an array of all keys from the cache table. */
  getAllKeys(): Key<H>[] {
    const keys: Key<H>[] = [];
    for (const key of this.expireTimes.keys()) {
      keys.push(this.hasher.unhash(key));
    }
    return keys;
  }

  /** Get an array of all keys from the cache table, without unhashing them. */
  getAllHashedKeys(): string[] {
    return [...this.expireTimes.keys()];
  }

  /** Get an array of only unexpired keys from the cache table. */
  getUnexpiredKeys(): Key<H>[] {
    const keys: Key<H>[] = [];
    for (const [key, expires] of this.expireTimes) {
      if (expires === false || !this.clock.isBeforeNow(expires)) {
        keys.push(this.hasher.unhash(key));
      }
    }
    return keys;
  }

  /** Get an array of only unexpired keys from the cache table, without unhashing them. */
  getUnexpiredHashedKeys(): string[] {
    const keys: string[] = [];
    for (const [key, expires] of this.expireTimes) {
      if (expires === false || !this.clock.isBeforeNow(expires)) {
        keys.push(key);
      }
    }
    return keys;
  }
}
